import { useCallback, useEffect, useRef, useState } from "react";
import { adminService } from "../../features/admin/service";
import { parseApiError } from "../../lib/http/apiErrors";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import type { AdminPromptDbOverride, AdminPromptEntry, HelpKbIndexStatus, HelpKbStatus, HelpKbTarget } from "../../features/admin/types";
import { PromptStepCard, type StepState } from "./AdminPromptPlaygroundPage";

const STATUS_STYLES: Record<HelpKbIndexStatus, string> = {
  uploaded: "bg-blue-900/30 text-blue-300 border border-blue-700/30",
  chunking: "bg-amber-900/30 text-amber-300 border border-amber-700/30",
  indexed: "bg-emerald-900/30 text-emerald-300 border border-emerald-700/30",
  failed: "bg-red-900/30 text-red-300 border border-red-700/30",
};

// Reindex is only meaningful once a source has been saved for this target —
// poll while an in-flight (re)index is chunking so the status card updates
// itself without the admin needing to refresh.
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 40; // ~100s ceiling before we stop auto-polling

interface HelpKbCardProps {
  target: HelpKbTarget;
  title: string;
  icon: string;
}

function HelpKbCard({ target, title, icon }: HelpKbCardProps) {
  const [mode, setMode] = useState<"text" | "pdf">("text");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [status, setStatus] = useState<HelpKbStatus | null>(null);
  const [statusFound, setStatusFound] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexError, setReindexError] = useState<string | null>(null);

  const { showError } = useAdminToast();
  useEffect(() => { if (saveError) showError(saveError); }, [saveError, showError]);
  useEffect(() => { if (reindexError) showError(reindexError); }, [reindexError, showError]);

  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const loadStatus = useCallback(async (attempt = 0) => {
    if (attempt === 0) setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await adminService.getHelpKbStatus(target);
      if (!mountedRef.current) return;
      setStatus(res);
      setStatusFound(true);
      if (res.status === "chunking" && attempt < POLL_MAX_ATTEMPTS) {
        pollRef.current = setTimeout(() => loadStatus(attempt + 1), POLL_INTERVAL_MS);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const axErr = err as { response?: { status?: number } };
      if (axErr?.response?.status === 404) {
        setStatus(null);
        setStatusFound(false);
      } else {
        setStatusError(parseApiError(err).message);
      }
    } finally {
      if (mountedRef.current && attempt === 0) setStatusLoading(false);
    }
  }, [target]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  function refreshAfterMutation() {
    if (pollRef.current) clearTimeout(pollRef.current);
    loadStatus();
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      if (mode === "text") {
        if (!text.trim()) throw new Error("Paste some text first.");
        await adminService.saveHelpKbText(target, text);
      } else {
        if (!file) throw new Error("Choose a PDF first.");
        await adminService.saveHelpKbPdf(target, file);
      }
      refreshAfterMutation();
    } catch (err) {
      setSaveError(err instanceof Error && !("response" in err) ? err.message : parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReindex() {
    setReindexing(true);
    setReindexError(null);
    try {
      await adminService.reindexHelpKb(target);
      refreshAfterMutation();
    } catch (err) {
      setReindexError(parseApiError(err).message);
    } finally {
      setReindexing(false);
    }
  }

  function pickFile(f: File | null) {
    if (f && f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      setSaveError("Only PDF files are accepted.");
      return;
    }
    setSaveError(null);
    setFile(f);
  }

  const canReindex = statusFound && !!status;

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-white/8 flex items-center gap-2">
        <span className="text-base leading-none">{icon}</span>
        <span className="text-sm font-bold text-white">{title}</span>
      </div>

      <div className="p-4 space-y-4 flex-1">
        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1 bg-white/5 rounded-xl border border-white/8 w-fit">
          <button
            onClick={() => setMode("text")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${mode === "text" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
          >
            Paste text
          </button>
          <button
            onClick={() => setMode("pdf")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${mode === "pdf" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
          >
            Upload PDF
          </button>
        </div>

        {mode === "text" ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder={`Paste the raw ${target} help/support content here…`}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 resize-none font-mono"
          />
        ) : (
          <div
            className={`rounded-xl border border-dashed overflow-hidden transition-colors ${
              dragOver ? "border-amber-500/60 bg-amber-950/20" : "border-amber-700/40 bg-amber-950/10"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) pickFile(f);
            }}
          >
            <div className="px-4 py-2.5 border-b border-amber-700/20 flex items-center gap-2">
              <span className="text-amber-400 text-sm">📎</span>
              <span className="text-xs font-semibold text-amber-300">Upload {target} help doc (PDF)</span>
            </div>
            <div className="px-4 py-4 flex items-center gap-4">
              <button
                onClick={() => inputRef.current?.click()}
                className="shrink-0 flex flex-col items-center justify-center gap-1.5 w-32 h-20 rounded-xl border border-dashed border-white/15 bg-white/3 hover:bg-white/6 hover:border-amber-600/40 transition-colors cursor-pointer"
              >
                <span className="text-xl">📂</span>
                <span className="text-[10px] text-gray-400 text-center leading-tight">Click or drag<br />PDF here</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; pickFile(f ?? null); e.target.value = ""; }}
              />
              {file ? (
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400 text-sm">✓</span>
                    <span className="text-xs font-semibold text-emerald-300 truncate">{file.name}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{(file.size / 1024).toFixed(0)} KB — ready to save</div>
                </div>
              ) : (
                <div className="text-[11px] text-gray-600 leading-relaxed">PDF only</div>
              )}
            </div>
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-2 text-xs text-red-300">{saveError}</div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving || (mode === "text" ? !text.trim() : !file)}
            className="flex-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg px-4 py-2.5 transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={handleReindex}
            disabled={reindexing || !canReindex}
            title={!canReindex ? "Save a text or PDF source first" : "Rebuild the search index from the currently saved source"}
            className="flex-1 border border-white/15 hover:bg-white/8 disabled:opacity-40 disabled:cursor-not-allowed text-gray-200 text-xs font-bold rounded-lg px-4 py-2.5 transition-colors"
          >
            {reindexing ? "Reindexing…" : "Reindex"}
          </button>
        </div>
        {reindexError && (
          <div className="rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-2 text-xs text-red-300">{reindexError}</div>
        )}
        {!canReindex && !statusLoading && (
          <p className="text-[10px] text-gray-600">Nothing saved yet for {target} — Save a source before reindexing.</p>
        )}
      </div>

      {/* Status footer */}
      <div className="px-4 py-3 border-t border-white/8 bg-black/20">
        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Current status</div>
        {statusLoading ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : statusError ? (
          <div className="text-xs text-red-300">{statusError}</div>
        ) : !statusFound || !status ? (
          <div className="text-xs text-gray-600">No source saved yet.</div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${STATUS_STYLES[status.status]}`}>
                {status.status}
              </span>
              {status.source_type && (
                <span className="text-[10px] text-gray-500">via {status.source_type === "pdf" ? "PDF" : "pasted text"}</span>
              )}
              {status.file_name && <span className="text-[10px] text-gray-500 truncate">— {status.file_name}</span>}
            </div>
            {status.status === "indexed" && (
              <div className="text-[11px] text-gray-400">
                {status.chunk_count ?? 0} chunk{status.chunk_count === 1 ? "" : "s"} · last indexed {formatISTDateTime(status.indexed_at)}
              </div>
            )}
            {status.status === "failed" && status.error_message && (
              <div className="text-[11px] text-red-300 bg-red-900/20 border border-red-700/30 rounded-lg px-2 py-1.5 mt-1">
                {status.error_message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface HelpKbSectionProps {
  prompts: AdminPromptEntry[];
  stepStates: Record<string, StepState>;
  availableModels: { key: string; value: string }[];
  onUpdate: (promptId: string, patch: Partial<StepState>) => void;
  onRun: (promptId: string) => void;
  onOverrideSaved: (promptId: string, override: AdminPromptDbOverride) => void;
}

export default function HelpKbSection({ prompts, stepStates, availableModels, onUpdate, onRun, onOverrideSaved }: HelpKbSectionProps) {
  const sellerPromptId = "rag_generator_seller_help";
  const buyerPromptId = "rag_generator_buyer_help";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white leading-tight">Help / Support KB</h1>
        <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">
          Source content for the buyer and seller help/support RAG answers — paste text or upload a PDF, then
          reindex to make it searchable. Save and Reindex are independent actions.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <HelpKbCard target="seller" title="Seller Help Doc" icon="🧑‍💼" />
          <PromptStepCard
            step={{ promptId: sellerPromptId, label: "Seller Help Generation Prompt", roleGate: "seller" }}
            index={0}
            prompt={prompts.find((p) => p.id === sellerPromptId)}
            ss={stepStates[sellerPromptId]}
            availableModels={availableModels}
            onUpdate={(patch) => onUpdate(sellerPromptId, patch)}
            onRun={() => onRun(sellerPromptId)}
            onOverrideSaved={onOverrideSaved}
          />
        </div>
        <div className="space-y-4">
          <HelpKbCard target="buyer" title="Buyer Help Doc" icon="🧳" />
          <PromptStepCard
            step={{ promptId: buyerPromptId, label: "Buyer Help Generation Prompt", roleGate: "buyer" }}
            index={0}
            prompt={prompts.find((p) => p.id === buyerPromptId)}
            ss={stepStates[buyerPromptId]}
            availableModels={availableModels}
            onUpdate={(patch) => onUpdate(buyerPromptId, patch)}
            onRun={() => onRun(buyerPromptId)}
            onOverrideSaved={onOverrideSaved}
          />
        </div>
      </div>
    </div>
  );
}
